import Link from "next/link";
import { CareIndeedLogo } from "../icons/careindeed-logo";
import styles from "./footer.module.css";
import InlineButton from "../ui/inline-button";
import { contacts, socials } from "@/lib/data";

export interface FooterCIAppProps {
  id?: string;
  className?: string;
  helpCenter?: {
    title: string;
    description: string;
    linkText: string;
    linkUrl: string;
    target?: string;
  };
  videoTutorials?: {
    title: string;
    description: string;
    linkText: string;
    linkUrl: string;
    target?: string;
  };
}

const FooterCIApp = ({
  id = "footer",
  className = "",
  helpCenter = {
    title: "Help Center",
    description: "Get started faster with tips and how-tos.",
    linkText: "Learn More",
    linkUrl: "#",
    target: "_blank",
  },
  videoTutorials = {
    title: "Video Tutorials",
    description: "See it in action with quick walkthroughs.",
    linkText: "Watch Videos",
    linkUrl: "#",
    target: "_blank",
  },
}: FooterCIAppProps) => {
  const currentYear = new Date().getFullYear();

  return (
    <footer
      id={id}
      className={`flex w-full flex-col bg-white ${className} ${styles.footerContainer}`}
    >
      <div className="mx-auto w-full px-4 py-6 md:px-6 lg:max-w-lg lg:px-0 lg:py-10">
        <div className="flex w-full flex-col gap-6 md:justify-between lg:flex-row lg:gap-40 lg:px-6">
          <div className="flex min-w-[224px] flex-row items-start text-center md:min-w-fit">
            <div className="flex w-full flex-col items-center md:items-start">
              <CareIndeedLogo
                width={80}
                height={80}
              />
              <h2 className="mt-2 font-montserrat text-subheading-2 md:text-heading-3">
                Care Indeed App
              </h2>
              <p className="mt-2 text-body-2-300 text-neutral-400">
                {currentYear} Care Indeed, Inc. All Rights Reserved.
              </p>
            </div>
          </div>

          <div className="flex w-full flex-row justify-between gap-y-6 md:justify-between lg:w-fit lg:flex-col lg:justify-start">
            <div className="flex h-fit flex-col items-center text-center md:flex-row md:items-start md:text-left">
              <div className="text-secondary-500 lg:mr-4">
                <svg
                  className="size-8 md:size-10 lg:size-12"
                  width="40"
                  height="40"
                  viewBox="0 0 49 48"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <g clipPath="url(#clip0_3023_5920)">
                    <path
                      d="M24.5 44C35.5457 44 44.5 35.0457 44.5 24C44.5 12.9543 35.5457 4 24.5 4C13.4543 4 4.5 12.9543 4.5 24C4.5 35.0457 13.4543 44 24.5 44Z"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M24.5 32V24"
                      stroke="currentColor"
                      strokeWidth="3"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M24.5 16H24.52"
                      stroke="currentColor"
                      strokeWidth="3"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </g>
                  <defs>
                    <clipPath id="clip0_3023_5920">
                      <rect
                        width="48"
                        height="48"
                        fill="white"
                        transform="translate(0.5)"
                      />
                    </clipPath>
                  </defs>
                </svg>
              </div>
              <div className="flex flex-col items-center px-2 md:items-start lg:px-0">
                <h3 className="font-montserrat text-subheading-3-500 text-neutral-500 lg:text-heading-4">
                  {helpCenter.title}
                </h3>
                <p className="text-body-2-300 text-neutral-500 md:text-body-1-300">
                  {helpCenter.description}
                </p>
                <InlineButton
                  color="orange"
                  className="px-0 text-xs md:text-base"
                  href={helpCenter.linkUrl}
                  target={helpCenter.target}
                >
                  {helpCenter.linkText}
                  <svg
                    className="h-3 w-3 md:h-4 md:w-4"
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M5 12H19M19 12L12 5M19 12L12 19"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </InlineButton>
              </div>
            </div>

            <div className="flex h-fit flex-col items-center text-center md:flex-row md:items-start md:text-left">
              <div className="text-secondary-500 lg:mr-4">
                <svg
                  className="size-8 md:size-10 lg:size-12"
                  width="40"
                  height="40"
                  viewBox="0 0 49 48"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M45.5802 12.84C45.3426 11.8908 44.8587 11.0211 44.1775 10.3188C43.4962 9.61648 42.6417 9.10637 41.7002 8.84C38.2602 8 24.5002 8 24.5002 8C24.5002 8 10.7402 8 7.30017 8.92C6.35866 9.18637 5.50412 9.69648 4.82286 10.3988C4.1416 11.1011 3.65774 11.9708 3.42017 12.92C2.7906 16.4111 2.48264 19.9526 2.50017 23.5C2.47773 27.0741 2.7857 30.6426 3.42017 34.16C3.68209 35.0797 4.17678 35.9163 4.85645 36.589C5.53613 37.2616 6.37781 37.7476 7.30017 38C10.7402 38.92 24.5002 38.92 24.5002 38.92C24.5002 38.92 38.2602 38.92 41.7002 38C42.6417 37.7336 43.4962 37.2235 44.1775 36.5212C44.8587 35.8189 45.3426 34.9492 45.5802 34C46.2049 30.5352 46.5128 27.0207 46.5002 23.5C46.5226 19.9259 46.2146 16.3574 45.5802 12.84Z"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M20 30.04L31.5 23.5L20 16.96V30.04Z"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
              <div className="flex flex-col items-center px-2 md:items-start lg:px-0">
                <h3 className="font-montserrat text-subheading-3-500 text-neutral-500 lg:text-heading-4">
                  {videoTutorials.title}
                </h3>
                <p className="text-body-2-300 text-neutral-500 md:text-body-1-300">
                  {videoTutorials.description}
                </p>
                <InlineButton
                  color="orange"
                  className="px-0 text-xs md:text-base"
                  href={videoTutorials.linkUrl}
                  target={videoTutorials.target}
                >
                  {videoTutorials.linkText}
                  <svg
                    className="h-3 w-3 md:h-4 md:w-4"
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M5 12H19M19 12L12 5M19 12L12 19"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </InlineButton>
              </div>
            </div>
          </div>

          <div className="flex flex-1 flex-col justify-center px-2 text-center md:justify-start md:px-0 md:text-left">
            <p className="mb-2 font-montserrat text-subheading-3-500 text-neutral-500 lg:mb-4 lg:text-heading-4">
              Contact Us
            </p>
            <ul className="flex flex-col items-center justify-center gap-y-2 text-neutral-500 md:items-start md:justify-start lg:gap-y-4">
              {contacts.map((contact, index) => (
                <li
                  key={`contact-${contact.title}-${index}`}
                  className="flex w-fit flex-row items-start gap-2 font-roboto text-body-1-300 md:items-center"
                >
                  <span className="mt-1 md:mt-0">{contact.icon}</span>
                  <a
                    className="whitespace-nowrap md:whitespace-normal"
                    href={contact.url}
                    id={contact.id}
                  >
                    {contact.title}
                  </a>
                </li>
              ))}
              <li className="mt-2 flex flex-row items-center gap-4 lg:mt-0">
                {socials.map((social, index) => (
                  <Link
                    key={`social-${social.url}-${index}`}
                    href={social.url}
                    prefetch
                    className="flex size-8 items-center justify-center rounded-full border border-secondary-500 text-secondary-500"
                  >
                    <span className="sr-only">{social.name}</span>
                    {social.icon}
                  </Link>
                ))}
              </li>
            </ul>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default FooterCIApp;
